import { useQuery } from '@tanstack/react-query'
import { api } from '../../lib/api'
import styles from '../_shared/CrudPage.module.css'

interface ConformidadeQaiParametro {
  parametro: string
  total: number
  conformes: number
  nao_conformes: number
  conformidade_pct: number
  limite: Record<string, unknown>
}

interface OsTendenciaItem {
  dia: string
  total: number
}

interface DashboardResumo {
  os_por_status: Record<string, number>
  sla: { total_mes: number; concluidas_no_prazo: number; compliance_pct: number }
  qai: { total_analises: number; conformes: number; conformidade_pct: number }
}

const PARAMETRO_LABEL: Record<string, string> = {
  co2: 'CO₂ (ppm)',
  co: 'CO (ppm)',
  pm10: 'PM10 (µg/m³)',
  pm25: 'PM2.5 (µg/m³)',
  temperatura: 'Temperatura (°C)',
  umidade: 'Umidade (%)',
  velocidade_ar: 'Velocidade do ar (m/s)',
  fungos_total: 'Fungos totais (UFC/m³)',
  bacterias_total: 'Bactérias totais (UFC/m³)',
}

function useResumo() {
  return useQuery({
    queryKey: ['analytics', 'dashboard'],
    queryFn: async () => {
      const r = await api.get<DashboardResumo>('/analytics/dashboard/')
      return r.data
    },
    staleTime: 1000 * 60 * 5,
  })
}

function useTendencia(dias: number) {
  return useQuery({
    queryKey: ['analytics', 'tendencia', dias],
    queryFn: async () => {
      const r = await api.get<OsTendenciaItem[]>('/analytics/tendencia-os/', {
        params: { dias },
      })
      return r.data
    },
  })
}

function useConformidadeQai() {
  return useQuery({
    queryKey: ['analytics', 'qai-conformidade'],
    queryFn: async () => {
      const r = await api.get<ConformidadeQaiParametro[]>(
        '/analytics/qai-conformidade/',
      )
      return r.data
    },
  })
}

function MiniChart({ dados }: { dados: OsTendenciaItem[] }) {
  if (!dados || dados.length === 0) {
    return <p style={{ color: '#666', margin: 0 }}>Sem dados no período.</p>
  }
  const max = Math.max(...dados.map((d) => d.total), 1)
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 100 }}>
      {dados.map((d) => (
        <div
          key={d.dia}
          title={`${d.dia}: ${d.total} OS`}
          style={{
            flex: 1,
            background: '#1565C0',
            height: `${(d.total / max) * 100}%`,
            minHeight: 2,
            borderRadius: '2px 2px 0 0',
          }}
        />
      ))}
    </div>
  )
}

export default function RelatoriosPage() {
  const resumo = useResumo()
  const tendencia = useTendencia(30)
  const qai = useConformidadeQai()

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>Relatórios</h1>
          <p className={styles.subtitle}>
            Visão consolidada de OS, SLA e conformidade QAI (NBR 17.037:2023).
          </p>
        </div>
      </header>

      {/* KPIs principais */}
      <section
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: '1rem',
          marginBottom: '1rem',
        }}
      >
        <div
          style={{
            background: 'white',
            padding: '1rem',
            borderRadius: 8,
            boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
            borderLeft: '4px solid #1565C0',
          }}
        >
          <div style={{ fontSize: '0.75rem', color: '#666', fontWeight: 600 }}>
            SLA do mês
          </div>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: '#1565C0' }}>
            {resumo.data?.sla.compliance_pct.toFixed(1) ?? '—'}%
          </div>
          <div style={{ fontSize: '0.85rem', color: '#666' }}>
            {resumo.data?.sla.concluidas_no_prazo ?? 0} de{' '}
            {resumo.data?.sla.total_mes ?? 0} OS no prazo
          </div>
        </div>

        <div
          style={{
            background: 'white',
            padding: '1rem',
            borderRadius: 8,
            boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
            borderLeft: '4px solid #2E7D32',
          }}
        >
          <div style={{ fontSize: '0.75rem', color: '#666', fontWeight: 600 }}>
            Conformidade QAI
          </div>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: '#2E7D32' }}>
            {resumo.data?.qai.conformidade_pct.toFixed(1) ?? '—'}%
          </div>
          <div style={{ fontSize: '0.85rem', color: '#666' }}>
            {resumo.data?.qai.conformes ?? 0} de{' '}
            {resumo.data?.qai.total_analises ?? 0} análises conformes
          </div>
        </div>

        <div
          style={{
            background: 'white',
            padding: '1rem',
            borderRadius: 8,
            boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
            borderLeft: '4px solid #F57C00',
          }}
        >
          <div style={{ fontSize: '0.75rem', color: '#666', fontWeight: 600 }}>
            OS em execução
          </div>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: '#F57C00' }}>
            {resumo.data?.os_por_status.em_execucao ?? 0}
          </div>
          <div style={{ fontSize: '0.85rem', color: '#666' }}>
            {resumo.data?.os_por_status.aberta ?? 0} aguardando início
          </div>
        </div>
      </section>

      {/* Tendência de OS */}
      <section
        style={{
          background: 'white',
          padding: '1.25rem',
          borderRadius: 8,
          marginBottom: '1rem',
          boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
        }}
      >
        <h2 style={{ marginTop: 0, fontSize: '1rem' }}>
          Tendência de OS (últimos 30 dias)
        </h2>
        {tendencia.isLoading ? (
          <p style={{ color: '#666' }}>Carregando...</p>
        ) : (
          <MiniChart dados={tendencia.data ?? []} />
        )}
      </section>

      {/* Conformidade QAI por parâmetro */}
      <section
        style={{
          background: 'white',
          padding: '1.25rem',
          borderRadius: 8,
          boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
        }}
      >
        <h2 style={{ marginTop: 0, fontSize: '1rem' }}>
          Conformidade QAI por parâmetro
        </h2>
        {qai.isLoading ? (
          <p style={{ color: '#666' }}>Carregando...</p>
        ) : (qai.data?.length ?? 0) === 0 ? (
          <p style={{ color: '#666', margin: 0 }}>
            Sem análises QAI registradas até o momento.
          </p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ background: '#F5F5F5' }}>
                <th style={{ padding: '0.5rem', textAlign: 'left' }}>Parâmetro</th>
                <th style={{ padding: '0.5rem', textAlign: 'right' }}>Total</th>
                <th style={{ padding: '0.5rem', textAlign: 'right' }}>Conformes</th>
                <th style={{ padding: '0.5rem', textAlign: 'right' }}>Não conf.</th>
                <th style={{ padding: '0.5rem', textAlign: 'right' }}>%</th>
              </tr>
            </thead>
            <tbody>
              {qai.data?.map((p) => (
                <tr key={p.parametro} style={{ borderTop: '1px solid #EEE' }}>
                  <td style={{ padding: '0.5rem' }}>
                    {PARAMETRO_LABEL[p.parametro] ?? p.parametro}
                  </td>
                  <td style={{ padding: '0.5rem', textAlign: 'right' }}>{p.total}</td>
                  <td
                    style={{
                      padding: '0.5rem',
                      textAlign: 'right',
                      color: '#2E7D32',
                      fontWeight: 600,
                    }}
                  >
                    {p.conformes}
                  </td>
                  <td
                    style={{
                      padding: '0.5rem',
                      textAlign: 'right',
                      color: p.nao_conformes > 0 ? '#C62828' : '#666',
                      fontWeight: p.nao_conformes > 0 ? 600 : 400,
                    }}
                  >
                    {p.nao_conformes}
                  </td>
                  <td
                    style={{
                      padding: '0.5rem',
                      textAlign: 'right',
                      fontWeight: 600,
                      color:
                        p.conformidade_pct >= 90
                          ? '#2E7D32'
                          : p.conformidade_pct >= 70
                            ? '#F57C00'
                            : '#C62828',
                    }}
                  >
                    {p.conformidade_pct.toFixed(1)}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  )
}
